import { useMemo } from 'react';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import type { PeriodoBot } from '@/services/api/analytics-bot';
import { PERIODOS } from '../format';
import { paisDaPagina } from '../paises';
import FiltroUsuarioAdmin from '@/components/FiltroUsuarioAdmin';

interface FiltrosBotProps {
    periodo: PeriodoBot;
    /** Datas do período personalizado (AAAA-MM-DD). */
    de: string;
    ate: string;
    hoje: string;
    paginaId: string | null;
    pais: string | null;
    dominio: string | null;
    userId: number | null;
    isAdmin: boolean;
    paginas: MetaPage[];
    /** Só países com dado no período (vem dos recortes por país). */
    paises: { codigo: string; nome: string }[];
    /** Só domínios com dado no período (vem dos recortes por domínio). */
    dominios: string[];
    onPeriodo: (p: PeriodoBot) => void;
    onDe: (v: string) => void;
    onAte: (v: string) => void;
    onPagina: (v: string | null) => void;
    onPais: (v: string | null) => void;
    onDominio: (v: string | null) => void;
    onLimpar: () => void;
}

/**
 * Filtros globais: valem para a tela inteira ao mesmo tempo (guia §3).
 * País e domínio saem das próprias páginas, pela mesma regra do backend.
 */
export default function FiltrosBot({
    periodo, de, ate, hoje, paginaId, pais, dominio, userId, isAdmin, paginas, paises, dominios,
    onPeriodo, onDe, onAte, onPagina, onPais, onDominio, onLimpar,
}: FiltrosBotProps) {
    const doUsuario = useMemo(
        () => (userId ? paginas.filter(p => p.user_id === userId) : paginas),
        [paginas, userId],
    );


    const doPais = useMemo(
        () => (pais ? doUsuario.filter(p => paisDaPagina(p.language)?.codigo === pais) : doUsuario),
        [doUsuario, pais],
    );

    const paginasOrd = useMemo(
        () => [...doPais].sort((a, b) => (a.page_name || '').localeCompare(b.page_name || '', 'pt-BR')),
        [doPais],
    );


    const temFiltro = !!pais || !!paginaId || !!dominio || periodo !== 'hoje';

    return (
        <div className="bar">
            <span className="bar-l">Período</span>
            <div className="seg">
                {PERIODOS.map(p => (
                    <button key={p.k} type="button" data-on={periodo === p.k ? 1 : 0} onClick={() => onPeriodo(p.k)}>
                        {p.l}
                    </button>
                ))}
            </div>
            {periodo === 'custom' && (
                <>
                    <input type="date" value={de} max={ate} onChange={e => e.target.value && onDe(e.target.value)} aria-label="Data inicial" />
                    <span className="muted" style={{ fontSize: 12 }}>até</span>
                    <input type="date" value={ate} min={de} max={hoje} onChange={e => e.target.value && onAte(e.target.value)} aria-label="Data final" />
                </>
            )}
            <div className="divider" />

            {isAdmin && (
                <>
                    <span className="bar-l">Usuário</span>
                    <FiltroUsuarioAdmin className="w-52" onChange={() => onPagina(null)} />
                </>
            )}

            <span className="bar-l">Página</span>
            <select value={paginaId ?? ''} aria-label="Filtrar por página" onChange={e => onPagina(e.target.value || null)}>
                <option value="">Todas</option>
                {paginasOrd.map(p => <option key={p.id} value={p.page_id}>{p.page_name}</option>)}
            </select>

            <span className="bar-l">País</span>
            <select
                value={pais ?? ''}
                aria-label="Filtrar por país"
                onChange={e => {
                    // A página escolhida provavelmente não é do país novo.
                    onPais(e.target.value || null);
                    onPagina(null);
                }}
            >
                <option value="">Todos</option>
                {paises.map(p => <option key={p.codigo} value={p.codigo}>{p.nome}</option>)}
            </select>

            <span className="bar-l">Domínio</span>
            <select value={dominio ?? ''} aria-label="Filtrar por domínio" onChange={e => onDominio(e.target.value || null)}>
                <option value="">Todos</option>
                {dominios.map(d => <option key={d} value={d}>{d}</option>)}
            </select>

            {temFiltro && (
                <button type="button" className="clearall" onClick={onLimpar}>Limpar filtros</button>
            )}
        </div>
    );
}
